import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Disc3, Mic2, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/StatusBadge";
import { Field } from "@/components/wizard/Field";
import { ImportSearch } from "@/components/import/ImportSearch";
import { t, fmtDate } from "@/i18n";

const Info = ({ label, value, testId }) => (
  <div data-testid={testId}><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t(label)}</p><p className="font-medium text-sm break-words">{value || "—"}</p></div>
);

const ReleaseImport = () => {
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const lookup = async (url) => {
    setBusy(true); setP(null);
    try { const { data } = await api.post("/import/lookup", { url }); setP(data); setTimeout(() => document.querySelector("[data-testid='import-preview']")?.scrollIntoView({ behavior: "smooth" }), 50); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const create = async () => {
    setBusy(true);
    try { const { data } = await api.post("/import/create", { preview: p }); toast.success(t("Lanzamiento importado")); nav(`/lanzamientos/${data.id}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="release-import">
      <ImportSearch type="album" onPick={lookup} placeholder={t("Nombre del álbum, canción o artista…")} hint="Escribe el nombre y elige el resultado. También puedes pegar un enlace de Spotify o Apple Music. Se extraen portada 3000×3000, UPC, ISRC, créditos, año y fecha original." />
      {busy && !p && <p className="text-sm text-muted-foreground" data-testid="import-loading">{t("Obteniendo datos del lanzamiento…")}</p>}
      {p && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="import-preview">
          <div className="lg:col-span-4 space-y-3">
            {p.cover_url ? <img src={p.cover_url} alt="" className="w-full aspect-square object-cover rounded-lg border border-border" data-testid="import-cover" /> : <div className="w-full aspect-square rounded-lg bg-muted grid place-items-center text-sm text-muted-foreground">{t("Sin portada")}</div>}
            <p className="text-xs text-muted-foreground" data-testid="import-cover-source">{p.cover_source === "apple" ? t("Portada de Apple Music a 3000×3000 px") : p.cover_source === "spotify" ? t("Portada de Spotify (640 px); se ajustará a 3000×3000 al importar") : t("Sin portada disponible")}</p>
            <div className="flex flex-wrap gap-3 text-xs">
              {p.spotify_url && <a href={p.spotify_url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">Spotify <ExternalLink className="h-3 w-3" /></a>}
              {p.apple_url && <a href={p.apple_url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">Apple Music <ExternalLink className="h-3 w-3" /></a>}
            </div>
          </div>
          <div className="lg:col-span-8 space-y-5">
            {p.warning && <p className="text-sm text-amber-300 inline-flex items-center gap-2" data-testid="import-warning"><AlertTriangle className="h-4 w-4" /> {p.warning}</p>}
            <div>
              <p className="eyebrow">{p.release_type}</p>
              <h2 className="font-display text-2xl font-bold" data-testid="import-title">{p.title}</h2>
              <p className="text-muted-foreground" data-testid="import-artists">{p.primary_artists.join(", ")}{p.featured_artists.length ? ` feat. ${p.featured_artists.slice(0, 5).join(", ")}` : ""}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Info label="UPC" value={p.upc} testId="import-upc" />
              <Info label="Fecha original" value={p.release_date ? (p.release_date.length === 10 ? fmtDate(p.release_date + "T12:00:00") : p.release_date) : null} testId="import-date" />
              <Info label="Año" value={p.copyright_year} testId="import-year" />
              <Info label="Sello" value={p.label} testId="import-label" />
              <Info label="Género" value={p.genre} />
              <Info label="Línea ℗" value={p.p_line} />
              {p.c_line && <Info label="Línea ©" value={p.c_line} />}
            </div>
            <Table data-testid="import-tracks">
              <TableHeader><TableRow><TableHead>#</TableHead><TableHead>{t("Canción")}</TableHead><TableHead>ISRC</TableHead><TableHead>{t("Artistas")}</TableHead><TableHead>{t("Compositores")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {p.tracks.map((tr) => (
                  <TableRow key={tr.track_number}>
                    <TableCell className="text-muted-foreground text-xs">{tr.track_number}</TableCell>
                    <TableCell className="font-medium">{tr.title}{tr.explicit && <span className="ml-1 text-[10px] text-muted-foreground">E</span>}</TableCell>
                    <TableCell className="font-mono text-xs">{tr.isrc || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{tr.artists.map((a) => a.name).join(", ")}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{tr.composers?.length ? tr.composers.join(", ") : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground">{p.credits_source ? t("Compositores tomados de Apple Music; los productores no están disponibles públicamente y se pueden completar en el lanzamiento.") : t("Apple Music no publica créditos para este álbum; podrás completarlos en el lanzamiento.")}</p>
            <div className="flex justify-end"><Button onClick={create} disabled={busy} className="gap-2" data-testid="import-create"><CheckCircle2 className="h-4 w-4" /> {t("Importar al catálogo")}</Button></div>
          </div>
        </div>
      )}
    </div>
  );
};

const ArtistImport = () => {
  const nav = useNavigate();
  const [apple, setApple] = useState("");
  const [r, setR] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const lookup = async (picked, source) => {
    setBusy(true);
    const prev = r;
    setR(null);
    const sp = source === "spotify" || /spotify/.test(picked);
    const body = sp ? { spotify: picked, apple: apple || prev?.apple_id || "" } : { spotify: prev?.spotify_uri || "", apple: picked };
    try { const { data } = await api.post("/import/artist/lookup", body); setR(data); setName(data.name || ""); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const create = async () => {
    setBusy(true);
    try { const { data } = await api.post("/import/artist/create", { name, spotify_uri: r.spotify_uri, apple_id: r.apple_id }); toast.success(t(data.merged ? "Perfil existente actualizado con los IDs" : "Artista importado")); nav("/artistas"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="artist-import">
      <ImportSearch type="artist" onPick={lookup} placeholder={t("Nombre del artista…")} hint="Escribe el nombre y elige el artista en Spotify y/o en Apple Music: puedes elegir uno de cada columna y se combinan en el mismo perfil. También acepta un URI de Spotify o el enlace/ID de Apple Music." />
      <div className="panel p-4 flex flex-wrap items-end gap-3">
        <Field label="ID o enlace de Apple Music (opcional)" hint="Si Apple no se detecta solo, pégalo aquí antes de buscar." className="flex-1 min-w-[260px]"><Input value={apple} onChange={(e) => setApple(e.target.value)} placeholder="27044968" data-testid="artist-apple" /></Field>
        {r && apple && <Button variant="outline" onClick={() => lookup(r.spotify_uri || apple)} disabled={busy} data-testid="artist-apple-apply">{t("Aplicar ID de Apple")}</Button>}
      </div>
      {busy && !r && <p className="text-sm text-muted-foreground" data-testid="artist-loading">{t("Obteniendo datos del artista…")}</p>}
      {r && (
        <div className="panel p-5 grid grid-cols-1 md:grid-cols-12 gap-5" data-testid="artist-preview">
          <div className="md:col-span-3">{r.image_url ? <img src={r.image_url} alt="" className="w-full aspect-square object-cover rounded-full border border-border" /> : <div className="w-full aspect-square rounded-full bg-muted" />}</div>
          <div className="md:col-span-9 space-y-4">
            <Field label="Nombre artístico"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="artist-name" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Info label="Spotify URI" value={r.spotify_uri ? <>{r.spotify_uri}{r.spotify_suggested && <span className="text-[10px] text-amber-300 ml-1">{t("(sugerido por nombre)")}</span>}</> : null} testId="artist-spotify-uri" />
              <Info label="Apple Music ID" value={r.apple_id ? <>{r.apple_id}{r.apple_suggested && <span className="text-[10px] text-amber-300 ml-1">{t("(sugerido por nombre)")}</span>}</> : null} testId="artist-apple-id" />
              <Info label="Seguidores en Spotify" value={r.spotify_followers != null ? new Intl.NumberFormat().format(r.spotify_followers) : null} />
              <Info label="Género" value={r.spotify_genres?.length ? r.spotify_genres.slice(0, 3).join(", ") : r.apple_genre} />
            </div>
            {r.existing_profile && <p className="text-sm text-amber-300" data-testid="artist-existing">{t("Ya existe el perfil «{n}» con estos IDs.", { n: r.existing_profile.name })}</p>}
            <div className="flex justify-end"><Button onClick={create} disabled={busy || !name.trim()} className="gap-2" data-testid="artist-create"><CheckCircle2 className="h-4 w-4" /> {t("Importar artista")}</Button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ImportPage() {
  return (
    <div className="space-y-6" data-testid="import-page">
      <PageHeader eyebrow="Catálogo" title="Importar" description="Trae lanzamientos y artistas desde Spotify y Apple Music con su portada en 3000×3000, UPC, ISRC, créditos, año y fecha original." />
      <Tabs defaultValue="release">
        <TabsList data-testid="import-tabs">
          <TabsTrigger value="release" className="gap-2" data-testid="tab-import-release"><Disc3 className="h-4 w-4" /> {t("Lanzamiento")}</TabsTrigger>
          <TabsTrigger value="artist" className="gap-2" data-testid="tab-import-artist"><Mic2 className="h-4 w-4" /> {t("Artista")}</TabsTrigger>
        </TabsList>
        <TabsContent value="release" className="mt-4"><ReleaseImport /></TabsContent>
        <TabsContent value="artist" className="mt-4"><ArtistImport /></TabsContent>
      </Tabs>
    </div>
  );
}
